import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, MessageCircle, Mail, Phone, CheckCircle2, AlertTriangle } from "lucide-react";
import { SiteHeader, SiteFooter } from "../components/SiteLayout";
import { buildWaUrl, openWhatsApp, WA_MSG } from "../lib/whatsapp";
import { formatApiError } from "../lib/api";
import SEO, { ORG_JSONLD, breadcrumb } from "../components/SEO";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contacto() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", reason: "", website: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.name.trim().length < 2) { setError("Indica tu nombre."); return; }
    // Teléfono OBLIGATORIO — el equipo contacta por WhatsApp
    const cleanPhone = form.phone.replace(/[\s\-()]/g, "");
    if (cleanPhone.length < 6 || !/^\+?\d+$/.test(cleanPhone)) {
      setError("Indica un teléfono válido — nos permite responderte por WhatsApp.");
      return;
    }
    if (form.reason.trim().length < 5) { setError("Cuéntanos brevemente el motivo."); return; }
    if (!consent) { setError("Debes aceptar la Política de Privacidad para enviar el mensaje."); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, consent }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data.detail) || "Error al enviar.");
      setDone(true);
    } catch (err) {
      setError(err.message || "No se pudo enviar el mensaje.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <SEO
        title="Contacto"
        description="Contacta con Fernando Marcos Subastas & Investment para asesoría profesional en subastas inmobiliarias del BOE. Respuesta en menos de 24 horas hábiles."
        path="/contacto"
        jsonLd={[
          ORG_JSONLD,
          breadcrumb([{ name: "Inicio", path: "/" }, { name: "Contacto", path: "/contacto" }]),
        ]}
      />
      <SiteHeader />

      <section className="bg-[#2d1f1a] text-white">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-3">Contacta conmigo</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] mb-3">
            Cuéntame tu caso. <span className="text-[#E0651F]">Te respondo personalmente.</span>
          </h1>
          <p className="text-stone-300 text-sm sm:text-base leading-relaxed max-w-3xl">
            Si tienes una duda concreta, prefieres explicarme tu situación por escrito o no encajas exactamente en ninguno de los servicios, escríbeme. Te respondo en un plazo máximo de <strong className="text-white">24 horas hábiles</strong>.
          </p>
        </div>
      </section>

      <main className="flex-1 max-w-5xl mx-auto px-6 py-14 w-full">
        {done ? (
          <div className="bg-white border-l-4 border-emerald-500 border border-stone-200 p-10 rounded-sm shadow-sm" data-testid="contact-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mb-4" />
            <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-emerald-700 mb-2">Mensaje enviado</div>
            <h2 className="font-display text-3xl font-black tracking-tight text-[#2d1f1a] mb-3">¡Gracias! Te respondo en menos de 24 h.</h2>
            <p className="text-stone-700 leading-relaxed mb-6">
              Hemos recibido tu mensaje y te confirmamos por email. Si quieres adelantar algún detalle, puedes escribirnos también por WhatsApp.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href={buildWaUrl(WA_MSG.generic)} target="_blank" rel="noopener noreferrer"
                onClick={(ev) => { ev.preventDefault(); openWhatsApp(WA_MSG.generic); }}
                className="inline-flex items-center gap-2 bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-6 py-3 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors" data-testid="contact-success-whatsapp">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <Link to="/" className="inline-flex items-center gap-2 text-[#E0651F] hover:underline px-2 py-3 text-xs tracking-[0.18em] uppercase font-bold">
                Volver al inicio <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-5 gap-8">
            <form onSubmit={onSubmit} className="lg:col-span-3 bg-white border border-stone-200 rounded-sm p-8 space-y-5" data-testid="contact-form">
              <Field label="Nombre y apellidos *">
                <input value={form.name} onChange={update("name")} required data-testid="input-name"
                  className="form-input" autoComplete="name" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email *">
                  <input type="email" value={form.email} onChange={update("email")} required data-testid="input-email"
                    className="form-input" autoComplete="email" />
                </Field>
                <Field label="Teléfono *">
                  <input type="tel" value={form.phone} onChange={update("phone")} required data-testid="input-phone"
                    className="form-input" placeholder="+34 600 123 456" autoComplete="tel"
                    inputMode="tel" pattern="[+0-9\s()\-]{6,}" />
                </Field>
              </div>
              <Field label="¿Cuál es el motivo de tu consulta? *">
                <textarea value={form.reason} onChange={update("reason")} required rows={6} data-testid="input-reason"
                  className="form-input resize-none"
                  placeholder="Cuéntanos brevemente tu situación, qué subasta te interesa o qué duda tienes..." />
              </Field>
              {/* Honeypot — hidden from humans, visible to bots */}
              <div style={{ position: "absolute", left: "-10000px", height: 0, overflow: "hidden" }} aria-hidden="true">
                <label>Sitio web<input tabIndex={-1} autoComplete="off" value={form.website} onChange={update("website")} /></label>
              </div>
              <label className="flex items-start gap-3 text-xs text-stone-600 leading-relaxed cursor-pointer" data-testid="consent-label">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 accent-[#E0651F]" data-testid="consent-check" required />
                <span>He leído y acepto la <a href="/politica-privacidad" target="_blank" rel="noopener noreferrer" className="text-[#E0651F] font-bold hover:underline">Política de Privacidad</a> y autorizo el tratamiento de mis datos para responder a mi consulta conforme al RGPD y a la LOPDGDD.</span>
              </label>
              {error && (
                <div className="flex items-start gap-3 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-sm" data-testid="contact-error">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              <button type="submit" disabled={loading} data-testid="contact-submit"
                className="bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-6 py-3.5 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors inline-flex items-center gap-2 disabled:opacity-60">
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando…</> : <>Enviar mensaje <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>

            <aside className="lg:col-span-2 space-y-5">
              <div className="bg-[#2d1f1a] text-white p-7 rounded-sm border-l-4 border-[#E0651F]">
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-[#E0651F] mb-3">Otras vías</div>
                <h3 className="font-display text-xl font-black tracking-tight mb-4 leading-tight">Si lo prefieres, contacta directamente.</h3>
                <div className="space-y-3 text-sm">
                  <a href={buildWaUrl(WA_MSG.generic)} target="_blank" rel="noopener noreferrer"
                    onClick={(ev) => { ev.preventDefault(); openWhatsApp(WA_MSG.generic); }}
                    className="flex items-center gap-3 text-stone-200 hover:text-white" data-testid="sidebar-whatsapp">
                    <MessageCircle className="w-4 h-4 text-[#E0651F]" /> WhatsApp
                  </a>
                  <a href="tel:+34695536486" className="flex items-center gap-3 text-stone-200 hover:text-white">
                    <Phone className="w-4 h-4 text-[#E0651F]" /> +34 695 536 486
                  </a>
                  <a href="mailto:fmsubastasinvestment@gmail.com" className="flex items-center gap-3 text-stone-200 hover:text-white">
                    <Mail className="w-4 h-4 text-[#E0651F]" /> fmsubastasinvestment@gmail.com
                  </a>
                </div>
              </div>
              <div className="bg-white border border-stone-200 p-6 rounded-sm">
                <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E0651F] mb-2">Respuesta garantizada</div>
                <p className="text-sm text-stone-700 leading-relaxed">
                  Te contestamos en un plazo máximo de <strong>24 horas hábiles</strong>. Si tu consulta es muy urgente, usa WhatsApp y te atendemos antes.
                </p>
              </div>
            </aside>
          </div>
        )}
      </main>

      <SiteFooter />
      <style>{`
        .form-input { width:100%; padding:0.65rem 0.85rem; font-size:0.9rem; background:#fff; border:1px solid #d6d3d1; border-radius:2px; outline:none; transition:border-color .15s, box-shadow .15s; }
        .form-input:focus { border-color:#E0651F; box-shadow:0 0 0 2px rgba(224,101,31,0.15); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.25em] uppercase font-bold text-stone-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
